/**
 * What this build is and where it lives.
 *
 * All three come from the environment at build time, because a preview deploy
 * and production run the same code from the same commit — the only difference
 * between them is these values and the hostname they answer on.
 */

/**
 * `production` is the one that gets indexed. Everything else is `preview`.
 *
 * **The default is `preview`, and that direction is deliberate.** Preview
 * deployments are real, public, crawlable URLs; a draft of the company's
 * marketing site competing with the real one in search results is expensive to
 * undo — Google has to be asked to forget it. So an unconfigured build is
 * `noindex` and wears the draft banner, and production has to say so out loud
 * (`VITE_SITE_ENV=production`, set on the production Worker and nowhere else).
 * Forgetting the flag costs a deploy; getting it backwards costs a reputation.
 */
export const SITE_ENV: 'production' | 'preview' =
  import.meta.env.VITE_SITE_ENV === 'production' ? 'production' : 'preview'

export const IS_PRODUCTION = SITE_ENV === 'production'

/**
 * Absolute origin, no trailing slash. Canonical and `hreflang` links have to be
 * absolute, so this is not cosmetic: pointing a preview's canonical at
 * production would tell search engines the draft *is* the live page.
 */
export const SITE_URL = (
  import.meta.env.VITE_SITE_URL ?? 'https://preview.digitopia.ai'
).replace(
  /\/$/,
  '',
)

/** The version label from the ledger — "v3", "v4". Shown in the preview banner. */
export const SITE_VERSION = import.meta.env.VITE_SITE_VERSION ?? 'dev'

/** Frozen at build time so server and client agree across a New Year boundary. */
export const BUILD_YEAR = import.meta.env.VITE_BUILD_YEAR ?? '2026'

/**
 * Cloudflare Turnstile's **site** key — the public half.
 *
 * A build-time constant and not a Worker var, because the widget is rendered by
 * the page and every page here is prerendered to static HTML at build time; a
 * `var` would never reach it. Public by design: Turnstile puts this key in the
 * markup of every site that uses it, and the half that proves anything is
 * `TURNSTILE_SECRET`, which lives only as a Worker secret.
 *
 * Empty means the widget does not render. Paired with the Worker's behaviour in
 * `worker/tools.ts`, that gives the two states worth having: on a preview the
 * form still works (the gate is the protection there), and in production the
 * Worker REFUSES rather than quietly accepting unprotected submissions.
 *
 * **The widget is `digitopia.ai forms`, Managed mode**, created 2026-08-26.
 * Managed rather than Invisible on purpose, and it is the mode this file's
 * consumer was already written for: `LeadGate` renders with
 * `appearance: 'interaction-only'`, so the widget is silent for almost everyone
 * and appears only when a challenge is genuinely needed. Invisible would delete
 * that last case rather than improve it — a real person wrongly scored as risky
 * would see "that check did not pass" with nothing they could do about it, on
 * the one form whose entire purpose is catching a rare and valuable lead.
 *
 * **Its hostname list is the apex only, and that is enough** — measured, not
 * assumed. Cloudflare's picker offers the account's zones and would not take
 * `www.` or `preview.` as separate entries, so a submission from
 * `preview.digitopia.ai` was the test: it passed on 2026-08-26, which means
 * Turnstile matches a domain together with its subdomains. Do not "fix" this by
 * adding `www.` and `preview.` — they are already covered, and a widget whose
 * hostname list disagrees with the one being used is how this fails silently.
 */
/**
 * Turnstile's public half.
 *
 * CORRECTED 2026-09-02: this value carried ONE EXTRA `A` from the day it was
 * written (`0x4AAAAAAAEcc…`, 25 characters, against the real
 * `0x4AAAAAAEcc…`, 24). Turnstile answered `400020 — invalid sitekey` and
 * rendered nothing, so no form or tool on the site ever produced a token.
 *
 * It went unnoticed for a week because of a combination worth remembering:
 * the widget is `appearance: 'interaction-only'`, so a widget that FAILED TO
 * RENDER and one that was never needed look exactly the same — nothing on
 * screen — and on a preview `turnstileProblem` treats a missing token as
 * "carry on", so nothing downstream complained either. The error was in the
 * browser console the whole time, and the code that would have surfaced it
 * (`error-callback`) was an empty function.
 *
 * If a form ever answers *"Please complete the check…"* again, read this value
 * against the dashboard character by character before looking anywhere else.
 * A key that is one character wrong fails identically to no key at all.
 */
export const TURNSTILE_SITE_KEY =
  import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '0x4AAAAAAEcc8qr3lEK0LC7j'
